import { fetchAuthSession } from 'aws-amplify/auth';
import { signOut } from 'aws-amplify/auth';

import { logger } from 'utils/logger';
import { ERROR_REASONS } from 'utils/logger/transports/constants';

import { checkNewNotification } from 'pages/notifications/helpers';

import config from 'config';

interface RequestOptions extends RequestInit {
    params?: Record<string, string | number | boolean>;
}

export class HttpError extends Error {
    status: number;
    statusText: string;
    data?: unknown;

    constructor(message: string, status: number, statusText: string, data?: unknown) {
        super(message);
        this.name = 'HttpError';
        this.status = status;
        this.statusText = statusText;
        this.data = data;
    }
}

const ALLOWED_HOSTS = [config.API_URL, config.PREVIEW_API_URL, config.PREVIEW_PRODUCT_API_URL]
    .filter((u): u is string => typeof u === 'string' && u.length > 0)
    .map((u) => new URL(u).hostname);
const ABSOLUTE_URL_REGEX = /^(https?:\/\/|ftp:\/\/)/;
const ALLOWED_PATHS = ['/admin/', '/feature/admin/'];
const ALLOWED_PROTOCOLS = ['http:', 'https:'];

function isAllowedUrl(url: string): boolean {
    try {
        const parsed = new URL(url);

        if (!ALLOWED_PROTOCOLS.includes(parsed.protocol)) return false;

        if (!ALLOWED_HOSTS.includes(parsed.hostname)) return false;

        const allowedPath = ALLOWED_PATHS.some((path) => parsed.pathname.startsWith(path));

        if (!allowedPath) return false;
        return true;
    } catch {
        return false;
    }
}
export const request = async <T = unknown>(input: RequestInfo | URL, options?: RequestOptions): Promise<T> => {
    const token = await getToken();
    const { url, fetchOptions } = prepareRequest(input, options, token);

    if (!isAllowedUrl(url)) {
        throw new HttpError('URL not whitelisted', 403, 'Forbidden');
    }
    /**
     * ⚠️ Semgrep SSRF false positive:
     * - This code runs in the frontend (browser environment)
     * - All requests are constructed relative to config.API_URL
     * - No possibility of SSRF exploitation (no access to internal server networks)
     */
    // Safe: frontend-only fetch, URL always validated
    // nosemgrep: nodejs_scan.javascript-ssrf-rule-node_ssrf
    const response = await fetch(url, { ...fetchOptions, redirect: 'manual' });
    await checkNewNotification(response);

    const contentType = response.headers.get('content-type');
    const isJson = contentType?.includes('application/json');

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const data = isJson ? await response.json().catch(() => null) : null;

    if (!response.ok) {
        // A 401 has two distinct sources:
        // 1. API Gateway rejects an expired/invalid token → body: {"message": "Unauthorized"}
        // 2. Lambda authorizer explicit deny (project/role mismatch, valid token) →
        //    body: {"Message": "User is not authorized to access this resource with an explicit deny"}
        // "not authorized" ≠ "unauthorized", so the substring check
        // correctly separates them. The token check is a fallback for empty/non-standard bodies.
        if (response.status === 401) {
            const rawMessage =
                typeof data === 'object' && data !== null
                    ? ((data as Record<string, unknown>).message ?? (data as Record<string, unknown>).Message)
                    : undefined;
            const isUnauthorized = typeof rawMessage === 'string' && rawMessage.toLowerCase().includes('unauthorized');

            let hasValidToken = false;
            try {
                const session = await fetchAuthSession();
                hasValidToken = !!session.tokens?.idToken;
            } catch {
                // treat session read failure as invalid token
            }

            if (isUnauthorized || !hasValidToken) {
                logger.warn('Signing out due to 401: token invalid or missing', {
                    tags: {
                        reason: ERROR_REASONS.unauthorizedSignOut,
                        requestUrl: url,
                        status: String(response.status),
                    },
                });
                await signOut();
                localStorage.clear();
            } else {
                logger.warn('401 without sign-out: permission or project denial', {
                    tags: {
                        reason: ERROR_REASONS.permissionDeny,
                        requestUrl: url,
                        status: String(response.status),
                    },
                });
            }
        }

        throw new HttpError(
            typeof data === 'object' && data !== null && 'message' in data && typeof (data as { message?: unknown }).message === 'string'
                ? (data as { message: string }).message
                : 'Request failed',
            response.status,
            response.statusText,
            data
        );
    }

    return data as T;
};

const getToken = async (): Promise<string | undefined> => {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString();
};

const prepareRequest = (input: RequestInfo | URL, options?: RequestOptions, token?: string): { url: string; fetchOptions: RequestInit } => {
    const { params, method, body, ...restOptions } = options ?? {};
    const headers = new Headers(restOptions.headers);

    if (token) {
        headers.set('Authorization', `Bearer ${token}`);
    }

    headers.set('x-api-key', config.API_KEY);
    headers.set('version', '1');

    const METHODS_WITH_BODY = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
    if (body && method && METHODS_WITH_BODY.has(method.toUpperCase()) && !headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
    }

    const url = buildUrl(input, params);

    return {
        url,
        fetchOptions: {
            ...restOptions,
            method,
            body,
            headers,
        },
    };
};

const buildUrl = (input: RequestInfo | URL, params?: Record<string, string | number | boolean>): string => {
    let inputStr: string;
    if (input instanceof URL) {
        inputStr = input.toString();
    } else if (typeof Request !== 'undefined' && input instanceof Request) {
        inputStr = input.url;
    } else if (typeof input === 'string') {
        inputStr = input;
    } else {
        throw new HttpError('Invalid input type for request URL', 400, 'Bad Request');
    }

    if (ABSOLUTE_URL_REGEX.test(inputStr)) {
        throw new HttpError('Absolute URLs are not allowed', 400, 'Bad Request');
    }

    const base = new URL(config.API_URL);
    if (!base.pathname.endsWith('/')) base.pathname += '/';

    const url = new URL(inputStr.replace(/^\/+/, ''), base);

    if (params) {
        Object.entries(params).forEach(([key, value]) => {
            url.searchParams.append(key, String(value));
        });
    }

    return url.toString();
};
